import { WEEKDAYS, EVENT_TONES } from '../constants/market-rankings-labels';

/**
 * 마켓 랭킹의 표시용 변환.
 *
 * ⚠️ 랭킹 인덱스의 날짜는 전부 `yyyyMMdd` 문자열이다 — Date로 바꾸는 자리는 여기 하나뿐이다.
 */

/** `20260818` → `08.18`. ⚠️ 8자리가 아니면 **원본을 그대로** 돌려준다 */
export function ymdToShort(ymd: unknown): string {
  const digits = String(ymd || '').replace(/\D/g, '');
  if (digits.length !== 8) return String(ymd || '');
  return `${digits.slice(4, 6)}.${digits.slice(6, 8)}`;
}

/** `yyyyMMdd` → 로컬 자정 Date. 8자리가 아니거나 없는 날이면 `null` */
function ymdToDate(ymd: unknown): Date | null {
  const digits = String(ymd || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const date = new Date(
    Number(digits.slice(0, 4)),
    Number(digits.slice(4, 6)) - 1,
    Number(digits.slice(6, 8)),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `20260728` → `2026.07.28 (화)` */
export function ymdToLong(ymd: unknown): string {
  const digits = String(ymd || '').replace(/\D/g, '');
  if (digits.length !== 8) return String(ymd || '');
  const date = ymdToDate(digits);
  const weekday = date ? ` (${WEEKDAYS[date.getDay()]})` : '';
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}${weekday}`;
}

/** 며칠 차이인가. 한쪽이라도 못 읽으면 `null` */
export function daysBetween(fromYmd: unknown, toYmd: unknown): number | null {
  const from = ymdToDate(fromYmd);
  const to = ymdToDate(toYmd);
  if (!from || !to) return null;
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

/**
 * 가격 표기 — **공용 포매터를 그대로 쓴다**(`utils/format/format-money`).
 *
 * ⚠️ 여기 사본을 두지 않는다. 아마존이 들어오며 달러가 섞였고, 통화를 모르는 표기는
 * $9.97 을 「10원」으로 보이게 한다. 통합 트렌드 검색도 같은 함수를 쓴다.
 * ⚠️ **두 번째 인자를 빠뜨리면 원화로 적힌다** — 부르는 쪽은 늘 `item.currency` 를 넘긴다.
 */
export { formatPrice } from 'src/utils/format/format-money';

/** 위와 같은 규칙. 단위만 없다 */
export function formatCount(value: unknown): string {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) return '';
  return Number(value).toLocaleString('ko-KR');
}

/**
 * 좁은 자리의 금액 — `만`·`천`으로 접는다.
 *
 * ⚠️ **원화 전용이다.** 「만·천」이 원화 감각이라 달러에 쓰면 뜻이 안 맞는다
 * (차트 축이 유일한 소비처이고, 그쪽은 원화 상품만 그린다 — `market-rankings-charts`).
 */
export function compactWon(value: unknown): string {
  const n = Number(value) || 0;
  if (n >= 10000) return `${(n / 10000).toFixed(1).replace(/\.0$/, '')}만`;
  if (n >= 1000) return `${Math.round(n / 1000)}천`;
  return n.toLocaleString('ko-KR');
}

/** 문구를 보고 배지 색을 고른다. 못 찾으면 무채색 */
export function eventTone(label: string): { color: string; bg: string } {
  const hit = EVENT_TONES.find((tone) => tone.re.test(label));
  return hit || { color: 'text.secondary', bg: 'grey.100' };
}

/** `1+1, 오특 · 베스트` → `['1+1', '오특']`. 쉼표와 가운뎃점 둘 다 구분자다 */
export function splitEventInfo(eventInfo: unknown, limit = 2): string[] {
  return String(eventInfo || '')
    .split(/[,·]/)
    .map((piece) => piece.trim())
    .filter(Boolean)
    .slice(0, limit);
}
